// Help contestuale: drawer flottante con articoli correlati alla pagina corrente
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { HelpCircle, X, ExternalLink, ChevronRight } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useHelp } from '../../hooks/useHelp'

// Mapping pagina → articoli
export const PAGE_HELP_MAPPING: Record<string, string[]> = {
  '/tickets/new': ['creare-un-nuovo-ticket', 'gestire-le-priorita'],
  '/tickets': ['gestire-i-ticket', 'filtri-avanzati'],
  '/reports': ['visualizzare-rapportini', 'scaricare-pdf'],
  '/reports/new': ['come-compilare-un-rapportino', 'compilare-offline'],
  '/warehouse': ['visualizzare-stock', 'scanner-barcode'],
  '/warehouse/materials': ['anagrafica-materiali'],
  '/warehouse/movements': ['registrare-movimenti'],
  '/hse': ['gestire-documenti-sicurezza', 'verificare-abilitazioni'],
  '/hse/documents': ['gestire-documenti-sicurezza'],
  '/hse/certifications': ['verificare-abilitazioni', 'scadenzario'],
  '/analytics': ['panoramica-analytics', 'esportare-dati'],
  '/settings': ['impostazioni-profilo', 'configurare-2fa'],
  '/settings/security': ['configurare-2fa'],
  '/gamification': ['sistema-punti', 'leaderboard'],
  '/integrations': ['configurare-integrazioni'],
  '/dashboard': ['panoramica-dashboard', 'kpi-principali'],
}

export function ContextualHelp() {
  const [isOpen, setIsOpen] = useState(false)
  const { articles, isLoading, hasHelp } = useHelp()

  if (!hasHelp) return null

  return (
    <>
      {/* Pulsante flottante */}
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          'fixed bottom-6 right-6 z-30 flex h-12 w-12 items-center justify-center rounded-full',
          'bg-[var(--fc-primary)] text-white shadow-[var(--fc-shadow-lg)]',
          'hover:bg-[var(--fc-primary-dark)] transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fc-primary)] focus-visible:ring-offset-2',
          'md:bottom-8 md:right-8'
        )}
        aria-label="Apri aiuto contestuale"
      >
        <HelpCircle className="h-6 w-6" />
      </button>

      {/* Drawer */}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/20" onClick={() => setIsOpen(false)} aria-hidden="true" />
          <div
            className={cn(
              'fixed bottom-6 right-6 z-50 w-80 rounded-xl border border-[var(--fc-border)]',
              'bg-[var(--fc-surface)] shadow-[var(--fc-shadow-lg)] overflow-hidden animate-fc-fade-in'
            )}
            role="dialog"
            aria-label="Aiuto contestuale"
          >
            <div className="flex items-center justify-between border-b border-[var(--fc-border)] px-4 py-3">
              <div className="flex items-center gap-2">
                <HelpCircle className="h-4 w-4 text-[var(--fc-primary)]" />
                <h3 className="text-sm font-semibold text-[var(--fc-foreground)]">Aiuto per questa pagina</h3>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)] transition-colors"
                aria-label="Chiudi"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-4">
              {isLoading ? (
                <div className="space-y-2">
                  {[1, 2].map((i) => (
                    <div key={i} className="h-12 animate-pulse rounded bg-[var(--fc-gray-200)]" />
                  ))}
                </div>
              ) : articles.length > 0 ? (
                <ul className="space-y-1" role="list">
                  {articles.map((article) => (
                    <li key={article.id}>
                      <Link
                        to={`/help/article/${article.slug}`}
                        className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-[var(--fc-foreground)] hover:bg-[var(--fc-surface-overlay)] transition-colors"
                        onClick={() => setIsOpen(false)}
                      >
                        <span className="flex-1">{article.title}</span>
                        <ChevronRight className="h-4 w-4 shrink-0 text-[var(--fc-foreground-subtle)]" />
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-[var(--fc-foreground-muted)]">
                  Nessun articolo disponibile per questa pagina.
                </p>
              )}

              <Link
                to="/help"
                className="mt-4 flex items-center gap-1 text-xs text-[var(--fc-primary)] hover:underline"
                onClick={() => setIsOpen(false)}
              >
                <ExternalLink className="h-3 w-3" />
                Centro assistenza completo
              </Link>
            </div>
          </div>
        </>
      )}
    </>
  )
}
